'use client';

import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Users, Award, Star, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const stats = [
  {
    icon: TrendingUp,
    value: 247,
    suffix: '+',
    label: 'Projects Delivered',
    description: 'Across all service lines since 2019',
  },
  {
    icon: Users,
    value: 83,
    suffix: '',
    label: 'Active Clients',
    description: 'Brands currently on retainer or in production',
  },
  {
    icon: Award,
    value: 14,
    suffix: '',
    label: 'Industry Awards',
    description: 'Regional and international creative recognition',
  },
  {
    icon: Star,
    value: 4.9,
    suffix: '/5',
    label: 'Client Satisfaction',
    description: 'Average rating across 200+ project reviews',
    isDecimal: true,
  },
  {
    icon: Zap,
    value: 6,
    suffix: ' yrs',
    label: 'Years Active',
    description: 'Continuously evolving since our founding',
  },
];

function useCountUp(target: number, isDecimal: boolean, duration = 1800, active: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = isDecimal
        ? parseFloat((eased * target).toFixed(1))
        : Math.floor(eased * target);
      setCount(current);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [active, target, duration, isDecimal]);

  return count;
}

function StatItem({
  stat,
  active,
  index,
}: {
  stat: typeof stats[0];
  active: boolean;
  index: number;
}) {
  const count = useCountUp(stat.value, stat.isDecimal || false, 1800, active);
  const Icon = stat.icon;

  return (
    <div
      className={`group flex flex-col items-center text-center px-6 py-8 relative transition-all duration-500 ${
        active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Divider (not on last) */}
      {index < stats.length - 1 && (
        <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-studio-700/50 hidden lg:block" />
      )}

      <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4 group-hover:bg-gold-500/20 group-hover:border-gold-500/40 transition-all duration-300">
        <Icon size={18} className="text-gold-400" />
      </div>

      <div className="font-display font-extrabold text-3xl xl:text-4xl text-white font-mono-data mb-1 tabular-nums">
        {stat.isDecimal ? count.toFixed(1) : count}
        <span className="text-gold-400">{stat.suffix}</span>
      </div>

      <div className="text-sm font-semibold text-studio-100 mb-1.5">{stat.label}</div>

      <p className="text-xs text-studio-400 leading-relaxed max-w-[140px]">{stat.description}</p>
    </div>
  );
}

export default function StatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-4 bg-studio-900 border-y border-studio-700/50"
      aria-label="Agency statistics"
    >
      <div className="absolute inset-0 animate-shimmer opacity-50" />
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0 divide-x divide-studio-700/30">
          {stats.map((stat, i) => (
            <StatItem key={stat.label} stat={stat} active={active} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}